import React from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useBreadcrumb } from '../context/BreadcrumbContext';

interface PageContext {
    title: string;
    url: string;
    id: string;
}

/**
 * Custom hook to extract page context from the current route
 * Used for feedback submission to capture page metadata
 */
export const usePageContext = (): PageContext => {
    const location = useLocation();
    const params = useParams();
    const { labels } = useBreadcrumb();

    // Extract page title from labels or pathname
    const getPageTitle = (): string => {
        const path = location.pathname;
        const segments = path.split('/').filter(Boolean);

        // For detail pages, try to get the label from the last segment
        if (segments.length > 0) {
            const lastSegment = segments[segments.length - 1];
            if (labels[lastSegment]) {
                return labels[lastSegment];
            }
        }

        // Fallback to page type
        if (path === '/topics') return 'Topics';
        if (path === '/thoughts') return 'Thoughts';
        if (path === '/quotes') return 'Quotes';
        if (path === '/passages') return 'Bible Passages';
        if (path === '/tags') return 'Tags';
        if (path === '/graph') return 'Graph View';
        if (path === '/random-wisdom') return 'Random Wisdom';
        if (path === '/starthere') return 'Start Here';

        return 'Unknown Page';
    };

    const title = getPageTitle();

    // Use current pathname as URL
    const url = location.pathname;

    // Extract ID from params or determine page type
    let id = '';
    if (params.id) {
        id = params.id;
    } else if (params.tag) {
        id = `tag_${params.tag}`;
    } else {
        // For list pages, use the page type as ID
        const path = location.pathname;
        if (path === '/topics') id = 'topics_list';
        else if (path === '/thoughts') id = 'thoughts_list';
        else if (path === '/quotes') id = 'quotes_list';
        else if (path === '/passages') id = 'passages_list';
        else if (path === '/tags') id = 'tags_list';
        else if (path === '/graph') id = 'graph_view';
        else if (path === '/random-wisdom') id = 'random_wisdom';
        else if (path === '/starthere') id = 'start_here';
        else id = 'unknown';
    }

    return { title, url, id };
};
